import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [pollState, setPollState] = useState("pending"); // pending | paid | failed
  const [form, setForm] = useState({ full_name: "", email: "", password: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPollState("paid");
          return;
        }
        if (r.data.status === "expired") {
          setPollState("failed");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 10) {
          setPollState("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= 10) {
          setPollState("failed");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await signup({
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        session_id: sessionId,
      });
      toast.success("Welcome! Your account is ready.");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col">
      <header className="container-bsp h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#1A1F3D] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#D4A853]" />
          </div>
          <span className="font-display text-xl text-[#1A1F3D]">BrandSnapPro™</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg bsp-card p-8 md:p-10" data-testid="checkout-success-card">
          {pollState === "pending" && (
            <div className="text-center py-10">
              <Loader2 className="w-10 h-10 text-[#D4A853] animate-spin mx-auto" />
              <h1 className="font-display text-2xl text-[#1A1F3D] mt-6">Confirming your payment…</h1>
              <p className="text-[#52525b] mt-2">Hang tight — this only takes a moment.</p>
            </div>
          )}
          {pollState === "failed" && (
            <div className="text-center py-10">
              <h1 className="font-display text-2xl text-[#1A1F3D]">Hmm, we couldn't confirm payment.</h1>
              <p className="text-[#52525b] mt-3">Please retry or contact support if you've been charged.</p>
              <Link to="/" className="btn-secondary mt-6 inline-flex" data-testid="back-home">Back to home</Link>
            </div>
          )}
          {pollState === "paid" && (
            <div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-7 h-7 text-[#D4A853]" />
                <span className="eyebrow">Payment confirmed</span>
              </div>
              <h1 className="font-display text-3xl text-[#1A1F3D] mt-3">Welcome! Create your account to get started.</h1>
              <p className="text-[#52525b] mt-2">Set up your login so you can access the system anytime.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Full name</label>
                  <input
                    className="bsp-input mt-1"
                    required
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    data-testid="signup-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Email</label>
                  <input
                    type="email"
                    className="bsp-input mt-1"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="signup-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Password</label>
                  <input
                    type="password"
                    className="bsp-input mt-1"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="signup-password"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Confirm password</label>
                  <input
                    type="password"
                    className="bsp-input mt-1"
                    required
                    minLength={8}
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    data-testid="signup-confirm"
                  />
                </div>
                <button type="submit" disabled={submitting} className="btn-primary w-full justify-center mt-2" data-testid="signup-submit">
                  {submitting ? "Creating account…" : "Create account & enter"}
                </button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
